import { Fragment, useRef, useState } from "react";
import { styled } from "@pigment-css/react";
import type { Sentiment } from "@acto/genui-components-schema";
import { useLocale, useTranslations } from "@acto/web-common/locales";
import {
  CardHeadline,
  HoverRowList,
  HoverRowSkeletonList,
  RowListCard,
  RowListEmptyState,
} from "@acto/taskpane/field-sales-2/components/primitives/Card";
import { Divider } from "@acto/taskpane/field-sales-2/components/primitives/Divider";
import { GlassButton } from "@acto/taskpane/field-sales-2/components/primitives/GlassButton";
import { MenuPopover } from "@acto/taskpane/field-sales-2/components/primitives/MenuPopover";
import { HeaderMoreButton } from "@acto/taskpane/field-sales-2/components/primitives/HeaderIconButton";
import { SentimentColor } from "@acto/taskpane/field-sales-2/components/patterns/genui-meeting-prep/SentimentColor";
import { DrillOverlay, useDrillStack } from "@acto/taskpane/field-sales-2/components/patterns/drilldown";
import {
  HiddenProductsDisclosure,
  ProductHideFlow,
  useHiddenAmong,
} from "@acto/taskpane/field-sales-2/components/patterns/product-hide";
import { usePeerPotentials, type PeerPotentialsItem } from "@acto/taskpane/field-sales-2/data-access/usePeerPotentials";
import { hideableProductsOfPeerRows } from "@acto/taskpane/field-sales-2/utils/productHide";
import { PeerPotentialsRow, PeerRowItem } from "./PeerPotentialsRow";

export interface PeerPotentialsTarget {
  clientId: string;
  /** The drill opens the union of these nodes. */
  productIds: string[];
  name: string | null;
  level: number;
  eyebrow: string;
  potentialText?: string | null;
  potentialSentiment?: Sentiment | null;
}

interface PeerLevel {
  productIds: string[];
  name: string | null;
  level: number;
}

const HeadlineRow = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "baseline",
  justifyContent: "space-between",
  gap: theme.fieldSalesTwo.spacing[12],
}));

const Potential = styled("span")(({ theme }) => ({
  flexShrink: 0,
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size["17"],
  fontWeight: theme.fieldSalesTwo.font.weight["500"],
  lineHeight: theme.fieldSalesTwo.font.lineHeight["140"],
  color: theme.fieldSalesTwo.colors.neutral[900],
}));

const ErrorBlock = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: theme.fieldSalesTwo.spacing[16],
  padding: `${theme.fieldSalesTwo.spacing[24]} 0`,
  textAlign: "center",
}));

const ErrorText = styled("p")(({ theme }) => ({
  margin: 0,
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size["15"],
  fontWeight: theme.fieldSalesTwo.font.weight["500"],
  lineHeight: theme.fieldSalesTwo.font.lineHeight["148"],
  color: theme.fieldSalesTwo.colors.neutral[700],
}));

const RetryButton = styled(GlassButton)(({ theme }) => ({
  color: theme.fieldSalesTwo.colors.neutral[800],
}));

interface PeerPotentialsDrilldownProps {
  target: PeerPotentialsTarget;
  onClose: () => void;
  /** When false, rows of hidden products are listed too. */
  canHideProducts?: boolean;
}

export function PeerPotentialsDrilldown({ target, onClose, canHideProducts = false }: PeerPotentialsDrilldownProps) {
  const t = useTranslations("fieldSalesTwo.peerPotentials");
  const tProducts = useTranslations("fieldSalesTwo.genuiMeetingPrep.productHide");
  const [isAdjusting, setIsAdjusting] = useState(false);
  const [confirmation, setConfirmation] = useState<{ id: number; message: string } | null>(null);
  const confirmationSeq = useRef(0);
  const { locale } = useLocale();
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const { stack, current, push, drillTo } = useDrillStack<PeerLevel>(
    { productIds: target.productIds, name: target.name, level: target.level },
    onClose,
  );

  const { isPending, error, data, refetch } = usePeerPotentials(target.clientId, current.productIds, current.level);

  const rows = data?.rows ?? [];
  const isSettled = !isPending && !error;
  const hideable = canHideProducts && isSettled ? hideableProductsOfPeerRows(rows) : [];
  const { isHidden, disclosure } = useHiddenAmong(hideable, (message) =>
    setConfirmation({ id: ++confirmationSeq.current, message }),
  );
  const visibleRows = rows.filter((row) => !isHidden([row.productId]));
  // urql keeps the previous level's data until the next one lands, so a settled result is the
  // only one that names the current level.
  const nodeName = (isPending || error ? null : data?.node.productName) ?? current.name ?? "";
  const adoptionDiffersBetweenRows = new Set(visibleRows.map((row) => row.peerAdoptionPercentage)).size > 1;
  const transitionKey = isPending ? "loading" : error ? "error" : rows.length === 0 ? "empty" : "list";

  const openRow = (row: PeerPotentialsItem) => {
    if (!row.hasChildren || !data) return;
    push({ productIds: [row.productId], name: row.productName, level: data.depth });
  };

  if (isAdjusting) {
    return (
      <ProductHideFlow
        products={hideable}
        talkingPointLabel={nodeName}
        onBack={() => setIsAdjusting(false)}
        returnsTo="drill"
        clientId={target.clientId}
      />
    );
  }

  const navStack = stack.map((level, index) => ({
    name: index === stack.length - 1 ? nodeName : (level.name ?? ""),
  }));

  return (
    <DrillOverlay
      scrollAreaRef={scrollAreaRef}
      eyebrow={target.eyebrow}
      headline={
        <HeadlineRow>
          <CardHeadline>{nodeName}</CardHeadline>
          {stack.length === 1 && target.potentialText && (
            <Potential>
              <SentimentColor sentiment={target.potentialSentiment ?? "neutral"}>{target.potentialText}</SentimentColor>
            </Potential>
          )}
        </HeadlineRow>
      }
      topBarAction={
        hideable.length > 0 ? (
          <MenuPopover
            trigger={<HeaderMoreButton aria-label={tProducts("drillOptions")} />}
            align="end"
            surface="list"
            ariaLabel={tProducts("drillOptions")}
            items={[
              {
                label: tProducts("menuAdjust"),
                onSelect: () => {
                  setConfirmation(null);
                  setIsAdjusting(true);
                },
              },
            ]}
          />
        ) : undefined
      }
      subheader={
        disclosure.products.length > 0 ? (
          <HiddenProductsDisclosure {...disclosure} triggerScrollRef={scrollAreaRef} leadingDivider />
        ) : undefined
      }
      stack={navStack}
      onDrillTo={drillTo}
      confirmation={confirmation}
      onConfirmationClose={() => setConfirmation(null)}
    >
      <RowListCard captionScrollRef={scrollAreaRef} transitionKey={transitionKey} caption={t("valueLabel")} nested>
        {isPending ? (
          <HoverRowSkeletonList Item={PeerRowItem} />
        ) : error ? (
          <ErrorBlock>
            <ErrorText>{t("error")}</ErrorText>
            <RetryButton type="button" onClick={refetch}>
              {t("retry")}
            </RetryButton>
          </ErrorBlock>
        ) : rows.length === 0 ? (
          <RowListEmptyState>{t("empty")}</RowListEmptyState>
        ) : (
          <HoverRowList role="list" aria-label={nodeName}>
            {visibleRows.map((row, index) => (
              <Fragment key={row.productId}>
                {index > 0 && <Divider />}
                <PeerPotentialsRow
                  row={row}
                  locale={locale}
                  showAdoption={adoptionDiffersBetweenRows}
                  onOpen={openRow}
                />
              </Fragment>
            ))}
          </HoverRowList>
        )}
      </RowListCard>
    </DrillOverlay>
  );
}
